import React, { useEffect, useMemo, useState } from 'react';
import PublicLayout, { PageHero } from '../../components/PublicLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowRightLeft, RefreshCw, Loader2, Globe } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { useLanguage } from '../../lib/LanguageContext';

const FLAGS = {
  EUR: '🇪🇺', USD: '🇺🇸', GBP: '🇬🇧', CHF: '🇨🇭', JPY: '🇯🇵', CAD: '🇨🇦',
  AUD: '🇦🇺', CNY: '🇨🇳', HKD: '🇭🇰', SEK: '🇸🇪', NOK: '🇳🇴', DKK: '🇩🇰',
  MAD: '🇲🇦', TND: '🇹🇳', AED: '🇦🇪', SGD: '🇸🇬', NZD: '🇳🇿', BRL: '🇧🇷',
  INR: '🇮🇳', ZAR: '🇿🇦', MXN: '🇲🇽', PLN: '🇵🇱', TRY: '🇹🇷', RUB: '🇷🇺',
};

const DEFAULT_CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF', 'JPY', 'CAD', 'AUD', 'CNY', 'HKD', 'SEK', 'NOK', 'DKK', 'MAD', 'TND', 'AED'];

export default function Devises() {
  const { t } = useLanguage();
  const [base, setBase] = useState('EUR');
  const [rates, setRates] = useState({});
  const [loadingRates, setLoadingRates] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const [amount, setAmount] = useState('100');
  const [from, setFrom] = useState('EUR');
  const [to, setTo] = useState('USD');
  const [result, setResult] = useState(null);
  const [converting, setConverting] = useState(false);

  const loadRates = async (b = base) => {
    setLoadingRates(true);
    try {
      const { data } = await api.get('/public/exchange-rates', { params: { base: b } });
      const r = data?.rates || data || {};
      setRates(r);
      setUpdatedAt(data?.updated_at || data?.timestamp || new Date().toISOString());
    } catch (e) {
      toast.error('Impossible de charger les taux de change');
    } finally {
      setLoadingRates(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadRates(base); }, [base]);

  const currencies = useMemo(() => {
    const list = Object.keys(rates || {});
    const merged = Array.from(new Set([base, ...DEFAULT_CURRENCIES, ...list]));
    return merged;
  }, [rates, base]);

  const convert = async () => {
    const a = parseFloat(amount);
    if (!a || a <= 0) { toast.error('Montant invalide'); return; }
    setConverting(true);
    try {
      const { data } = await api.get('/public/convert', { params: { from, to, amount: a } });
      const value = data?.result ?? data?.converted ?? data?.value ?? data?.amount;
      const rate = data?.rate ?? (rates && rates[to] && from === base ? rates[to] : null);
      setResult({ value: Number(value), rate });
    } catch (e) {
      // Fallback : derive from rates table
      try {
        if (from === base && rates[to]) {
          setResult({ value: a * rates[to], rate: rates[to] });
        } else if (to === base && rates[from]) {
          setResult({ value: a / rates[from], rate: 1 / rates[from] });
        } else if (rates[from] && rates[to]) {
          const r = rates[to] / rates[from];
          setResult({ value: a * r, rate: r });
        } else {
          toast.error('Conversion indisponible');
        }
      } catch {
        toast.error('Conversion indisponible');
      }
    } finally {
      setConverting(false);
    }
  };

  const swap = () => {
    setFrom(to); setTo(from); setResult(null);
  };

  return (
    <PublicLayout>
      <PageHero
        eyebrow={t('public.devisesEyebrow')}
        title={t('public.devisesTitle')}
        subtitle={t('public.devisesSubtitle')}
      />

      {/* Converter widget */}
      <section className="max-w-5xl mx-auto px-6 -mt-10 mb-12 relative z-10">
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xl dark:bg-slate-900 dark:border-slate-700">
          <div className="grid md:grid-cols-7 gap-3 items-end">
            <div className="md:col-span-2">
              <Label>Montant</Label>
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="input-convert-amount"/>
            </div>
            <div className="md:col-span-2">
              <Label>De</Label>
              <Select value={from} onValueChange={setFrom}>
                <SelectTrigger data-testid="select-from"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {currencies.map((c) => (
                    <SelectItem key={c} value={c}>{FLAGS[c] || '💱'} {c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-1 flex justify-center pb-1">
              <Button variant="outline" onClick={swap} size="icon" aria-label="Inverser" data-testid="btn-swap">
                <ArrowRightLeft className="w-4 h-4"/>
              </Button>
            </div>
            <div className="md:col-span-2">
              <Label>Vers</Label>
              <Select value={to} onValueChange={setTo}>
                <SelectTrigger data-testid="select-to"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {currencies.map((c) => (
                    <SelectItem key={c} value={c}>{FLAGS[c] || '💱'} {c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <Button onClick={convert} disabled={converting} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-convert">
              {converting ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <ArrowRightLeft className="w-4 h-4 mr-1"/>} Convertir
            </Button>
            {result !== null && (
              <div className="text-right">
                <div className="text-3xl font-bold tracking-tight">
                  {Number(result.value || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} <span className="text-slate-500">{to}</span>
                </div>
                {result.rate && <div className="text-xs text-slate-500 dark:text-slate-400">1 {from} = {Number(result.rate).toFixed(4)} {to}</div>}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Rates table */}
      <section className="max-w-5xl mx-auto px-6 pb-16">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="text-2xl font-bold">Taux de change actuels</h2>
            <p className="text-slate-500 text-sm dark:text-slate-400">Référencés par rapport à <strong>{base}</strong>{updatedAt && <> • mis à jour {new Date(updatedAt).toLocaleString('fr-FR')}</>}</p>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Devise de base</Label>
            <Select value={base} onValueChange={setBase}>
              <SelectTrigger className="w-32" data-testid="select-base"><SelectValue/></SelectTrigger>
              <SelectContent>
                {DEFAULT_CURRENCIES.map((c) => <SelectItem key={c} value={c}>{FLAGS[c] || '💱'} {c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={() => loadRates(base)} disabled={loadingRates}>
              {loadingRates ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}
            </Button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden dark:bg-slate-900 dark:border-slate-700">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase text-slate-600 dark:text-slate-300">
              <tr>
                <th className="text-left p-3">Devise</th>
                <th className="text-left p-3">Code</th>
                <th className="text-right p-3">1 {base} =</th>
                <th className="text-right p-3">1 unité = </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {loadingRates && <tr><td colSpan={4} className="p-8 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin mx-auto"/></td></tr>}
              {!loadingRates && Object.keys(rates).length === 0 && <tr><td colSpan={4} className="p-8 text-center text-slate-500">Aucun taux disponible</td></tr>}
              {!loadingRates && Object.entries(rates)
                .filter(([code]) => code !== base)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([code, rate]) => (
                  <tr key={code} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="p-3"><span className="text-lg mr-2">{FLAGS[code] || '💱'}</span>{currencyName(code)}</td>
                    <td className="p-3 font-mono">{code}</td>
                    <td className="p-3 text-right font-medium">{Number(rate).toFixed(4)}</td>
                    <td className="p-3 text-right text-slate-500 dark:text-slate-400">{(1 / Number(rate)).toFixed(4)} {base}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 grid md:grid-cols-3 gap-4">
          <Info icon={Globe} title="Taux en temps réel" desc="Mis à jour toutes les 10 minutes auprès de sources financières."/>
          <Info icon={ArrowRightLeft} title="Multi-devises" desc="EUR, USD, GBP, CHF, JPY, MAD, AED... plus de 30 devises."/>
          <Info icon={RefreshCw} title="Virement SWIFT" desc="Pour transférer dans une autre devise, utilisez notre service de virement SWIFT."/>
        </div>

        <p className="text-xs text-slate-500 dark:text-slate-400 mt-6">Les taux affichés sont indicatifs. Les taux appliqués lors d'un virement réel peuvent inclure une marge bancaire et des frais.</p>
      </section>
    </PublicLayout>
  );
}

function currencyName(code) {
  const names = {
    EUR: 'Euro', USD: 'Dollar US', GBP: 'Livre sterling', CHF: 'Franc suisse', JPY: 'Yen japonais',
    CAD: 'Dollar canadien', AUD: 'Dollar australien', CNY: 'Yuan chinois', HKD: 'Dollar de Hong Kong',
    SEK: 'Couronne suédoise', NOK: 'Couronne norvégienne', DKK: 'Couronne danoise',
    MAD: 'Dirham marocain', TND: 'Dinar tunisien', AED: 'Dirham émirien', SGD: 'Dollar de Singapour',
    NZD: 'Dollar néo-zélandais', BRL: 'Real brésilien', INR: 'Roupie indienne', ZAR: 'Rand sud-africain',
    MXN: 'Peso mexicain', PLN: 'Zloty polonais', TRY: 'Livre turque', RUB: 'Rouble russe',
  };
  return names[code] || code;
}

function Info({ icon: Icon, title, desc }) {
  return (
    <div className="p-4 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl">
      <div className="w-9 h-9 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 rounded-lg flex items-center justify-center"><Icon className="w-4 h-4"/></div>
      <div className="font-semibold text-sm mt-2">{title}</div>
      <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{desc}</div>
    </div>
  );
}
